/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable quotes */
/* eslint-disable react/no-unstable-nested-components */
/**
 * Routes Configuration
 * 
 * Main navigation structure using React Navigation
 * Defines:
 * - Screen stacks (Auth, Main, Root)
 * - Deep linking configuration for web
 * - Custom navigation bar
 * - Global UI components (Snackbar, Alert, Loader)
 * - Tablet detection and responsive layout
 * 
 * SCREEN STRUCTURE:
 * - Auth Stack: Login, ForgotPassword, OTP, ResetPassword
 * - Main Stack: Home, Canteen, Cart, Orders, OrderDetails, Reports, Settings
 * - Root: Init, Splash
 * 
 * @module Routes
 */
import * as React from 'react';
import {
    NavigationContainer,
    DefaultTheme as NavigationDefaultTheme,
    useNavigation,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { adaptNavigationTheme } from 'react-native-paper';
import Login from './screens/Login';
import ForgotPassword from './screens/ForgotPassword';
import OTP from './screens/OTP';
import ResetPassword from './screens/ResetPassword';
import { Dimensions, LayoutAnimation, Platform, StatusBar, UIManager } from 'react-native';
import { theme } from './theme/theme';
import navigationService from './services/NavigationService';
import Splash from './screens/Splash';
import SnackbarComp from './components/SnackbarComp';
import AppLoader from './components/AppLoader';
import AlertComp from './components/AlertComp';
import Canteen from './screens/Canteen';
import CustomNavigationBar from './components/CustomNavigationBar';
import Cart from './screens/Cart';
import Orders from './screens/Orders';
import OrderDetails from './screens/OrderDetails';
import withAuthCheck from './components/withAuthCheck';
import MyAccount from './screens/MyAccount';
import LogoutAlert from './components/LogoutAlert';
import ChangePassword from './screens/ChangePassword';
import Profile from './screens/Profile';
import SalesReport from './screens/SalesReport';
import ItemWiseReport from './screens/ItemWiseReport';
import CountersReport from './screens/CountersReport';
import BluetoothPrinter from './screens/BluetoothPrinter';
import Init from './screens/Init';
import { isElectron } from './services/Utils';
import ThermalPrinters from './screens/ThermalPrinters';
import { setIsTablet } from './store/info.slice';
import { useDispatch } from 'react-redux';
import Menu from './screens/Menu/Menu';
import ItemWiseStockReport from './screens/ItemWiseStockReport';
const { LightTheme } = adaptNavigationTheme({ reactNavigationLight: NavigationDefaultTheme });
const width = Dimensions.get('window').width;

if (Platform.OS === 'android') {
    UIManager.setLayoutAnimationEnabledExperimental(true);
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
}

const RootStack = createNativeStackNavigator();
/**
 * Navigation component to set navigation service reference
 */
const NavigationComponent = () => {
    const navigation = useNavigation();
    navigationService.navigation = navigation;
    return null;
};

/**
 * Deep linking configuration for web platform
 * Maps screen names to URL paths under /cash-counter/
 */
let config = {
    screens: {
        Splash: "/cash-counter/Splash",
        Login: "/cash-counter/Login",
        Home: "/cash-counter/Home",
        OTP: "/cash-counter/OTP",
        Canteen: "/cash-counter/Canteen",
        SearchItems: "/cash-counter/SearchItems",
        Cart: "/cash-counter/Cart",
        Orders: "/cash-counter/Orders",
        Profile: "/cash-counter/Profile",
        ChangePassword: "/cash-counter/ChangePassword",
        ForgotPassword: "/cash-counter/ForgotPassword",
        ResetPassword: "/cash-counter/ResetPassword",
        OrderDetails: "/cash-counter/OrderDetails",
        MyAccount: "/cash-counter/MyAccount",
        CountersReport: "/cash-counter/CountersReport",
        SalesReport: "/cash-counter/SalesReport",
        ItemWiseReport: "/cash-counter/ItemWiseReport",
        Menu: "/cash-counter/Menu",
    },
};
const linking = {
    config,
};

/**
 * Main Routes component
 * Sets up NavigationContainer with theme and deep linking
 * Detects tablet mode and dispatches to Redux
 */
export default function Routes() {
    const dispatch = useDispatch();
    React.useEffect(() => {
        dispatch(setIsTablet(width > 700));
    }, []);

    const navTheme = {
        ...LightTheme,
        colors: {
            ...LightTheme.colors,
            primary: theme.colors.primary,
        },
    };

    return (
        <NavigationContainer theme={navTheme} linking={linking}>
            <StatusBar backgroundColor={theme.colors.primary} barStyle="light-content" />
            <NavigationComponent />
            <SnackbarComp />
            <AppLoader />
            <AlertComp />
            <LogoutAlert />
            <RootStack.Navigator initialRouteName="Splash" screenOptions={{
                animation: Platform.OS === 'ios' ? 'default' : 'fade',
                gestureEnabled: true,
            }}>
                <RootStack.Screen name="Splash" component={Splash} options={{
                    header: (props) => null,
                }} />
                <RootStack.Screen name="Login" component={Login} options={{
                    header: (props) => null,
                }} />
                <RootStack.Screen name="OTP" component={OTP} options={{
                    header: (props) => <CustomNavigationBar {...props} title={'OTP Verification'} />,
                }} />
                <RootStack.Screen name="ForgotPassword" component={ForgotPassword} options={{
                    header: (props) => <CustomNavigationBar {...props} title={'Forgot Password'} />,
                }} />
                <RootStack.Screen name="ResetPassword" component={ResetPassword} options={{
                    header: (props) => <CustomNavigationBar {...props} title={'Reset Password'} />,
                }} />
                <RootStack.Screen name="Home" component={withAuthCheck(Init)} options={{
                    header: (props) => <CustomNavigationBar {...props} isHomeHeader={true} />,
                }} />
                <RootStack.Screen name="Canteen" component={withAuthCheck(Canteen)} options={{
                    header: (props) => <CustomNavigationBar {...props} showCanteenName={true} />,
                }} />
                <RootStack.Screen name="Cart" component={withAuthCheck(Cart)} options={{
                    header: (props) => <CustomNavigationBar {...props} title="Cart" />,
                }} />
                <RootStack.Screen name="Orders" component={withAuthCheck(Orders)} options={{
                    header: (props) => <CustomNavigationBar {...props} ordersRefreshIcon={Platform.OS === 'web'} title="Orders" />,
                }} />
                <RootStack.Screen name="OrderDetails" component={withAuthCheck(OrderDetails)} options={{
                    header: (props) => <CustomNavigationBar {...props} orderDetailRefreshIcon={Platform.OS === 'web'} title="Order" />,
                }} />
                <RootStack.Screen name="MyAccount" component={withAuthCheck(MyAccount)} options={{
                    header: (props) => <CustomNavigationBar {...props} title={"My Account"} showCustomerName={true} />,
                }} />
                <RootStack.Screen name="ChangePassword" component={withAuthCheck(ChangePassword)} options={{
                    header: (props) => <CustomNavigationBar {...props} title={'ChangePassword'} />,
                }} />
                <RootStack.Screen name="Profile" component={withAuthCheck(Profile)} options={{
                    header: (props) => <CustomNavigationBar {...props} title={"Profile"} />,
                }} />
                <RootStack.Screen name="SalesReport" component={withAuthCheck(SalesReport)} options={{
                    header: (props) => <CustomNavigationBar {...props} title="Sales Report" />,
                }} />
                <RootStack.Screen name="ItemWiseReport" component={withAuthCheck(ItemWiseReport)} options={{
                    header: (props) => <CustomNavigationBar {...props} title="Item Wise Report" />,
                }} />
                <RootStack.Screen name="ItemWiseStockReport" component={withAuthCheck(ItemWiseStockReport)} options={{
                    header: (props) => <CustomNavigationBar {...props} title="Item Wise Stock Report" />,
                }} />
                <RootStack.Screen name="CountersReport" component={withAuthCheck(CountersReport)} options={{
                    header: (props) => <CustomNavigationBar {...props} title="Counters Report" />,
                }} />
                <RootStack.Screen name="Menu" component={withAuthCheck(Menu)} options={{
                    header: (props) => <CustomNavigationBar {...props} title={"Menu"} />,
                }} />
                {Platform.OS === "android" && (
                    <RootStack.Screen name="BluetoothPrinter" component={withAuthCheck(BluetoothPrinter)} options={{
                        header: (props) => <CustomNavigationBar {...props} title="Printer" />,
                    }} />
                )}
                {isElectron() && (
                    <RootStack.Screen name="ThermalPrinters" component={withAuthCheck(ThermalPrinters)} options={{
                        header: (props) => <CustomNavigationBar {...props} title="Printer" />,
                    }} />
                )}
            </RootStack.Navigator>
        </NavigationContainer >
    );
}
